// CUSTODY SETTINGS (UI rebuild batch 4 · custody setup · reference build) — Astra's three groups moved off the daily Flow:
// Operating limits (the caps, the No caps switch, the second-device approval), Fee floors, Renewals; one "Review
// settings" that reads every change before → after, then one Save. No operational queue and no balances here.
//
// SERVER VALUES, NEVER FIXTURE DEFAULTS: every field is seeded from its read and only from it. A group whose read failed
// shows that failure and no fields; a group still reading shows "Reading…". Fee floors that are the seeded defaults say
// so ("Defaults until you set them"). The second-device approval is OFF unless the server says on (N-068); the caps stay
// on unless the server says No caps (db/0098 — on his word once testing ends); the whitelist's 10 USDT test is the
// server's figure and is not set here.
//
// Kept from screens/custody/Flow.tsx (Caps, FeeFloors, Renewals): the reads and keys (['custody','limits'],
// ['custody','fee-floors'], ['custody','renewals']), setLimits with the whole body (both merchant-only sources' caps,
// capsOff, secondDevicePayouts) and its refreshes (limits, whitelist, approvals), setFeeFloors over FEE_FLOOR_CLASSES,
// setRenewalDays per class with everyDaysProblem, Renew now behind renewConfirm, refused without a key for the class;
// limitsRefusal and renewalRefusal. Dropped from this screen: "Used today" (operational — Astra: no queues here).
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useEffect, useMemo, useState } from 'react';
import { reseed, settled, SEEDED_EMPTY, type Seeded } from '../../../lib/seeded-draft';
import { readIsCurrent } from '../../../lib/custody-screen';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { custody as api, type FeeFloorClass, type MoveLimits, type RenewalClass, type RenewalRow } from '../../../lib/api';
import { FEE_FLOOR_CLASSES } from '../../../../../contracts/wallet.mjs';
import { CLASS_WORD, groupDigits, relativeTime } from '../../../lib/custody-format';
import { renewConfirm, renewalRefusal, everyDaysProblem } from '../../../lib/custody-renewals';
import { limitsChanged, limitsRefusal } from '../../custody/refusals';
import { StateLine, Fact, TAP, Address } from '../../../components/v2/kit';
import { useRenewNow } from '../../../lib/renew-watch';
import { FormFrame, FormFoot, FormAside, PRIMARY, QUIET } from '../../../components/v2/move/FormFrame';
import { CA } from '../../../components/v2/custody-admin/paths';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);
const CLASSES = FEE_FLOOR_CLASSES as readonly FeeFloorClass[];

type Limits = { move: string; day: string; mMove: string; mDay: string; fMove: string; fDay: string; capsOff: boolean; twoDevices: boolean };
const limitsOf = (l: MoveLimits): Limits => ({
  move: l.moveCeilingUsdt, day: l.dayCeilingUsdt,
  mMove: l.sources?.dirty_hot?.moveCeilingUsdt ?? '', mDay: l.sources?.dirty_hot?.dayCeilingUsdt ?? '',
  fMove: l.sources?.frozen?.moveCeilingUsdt ?? '', fDay: l.sources?.frozen?.dayCeilingUsdt ?? '',
  capsOff: l.capsOff === true, twoDevices: l.secondDevicePayouts === true,
});
const LIMIT_WORDS: Record<keyof Limits, string> = {
  move: 'Per move cap', day: 'Daily cap', mMove: 'Merchant Hot per move', mDay: 'Merchant Hot daily',
  fMove: 'Flagged per move', fDay: 'Flagged daily', capsOff: 'No caps', twoDevices: 'Second device approval',
};
const say = (v: string | boolean, usdt = true): string => (typeof v === 'boolean' ? (v ? 'on' : 'off') : `${groupDigits(v.trim() || '—')}${usdt ? ' USDT' : ' TRX'}`);

type Change = { group: 'limits' | 'floors' | RenewalClass; label: string; before: string; after: string };
type Outcome = { group: Change['group']; ok: boolean; line: string };

/** `embedded`: drawn inside custody's Settings screen, under its head and tabs. */
export function CustodySettingsV2({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, refetchInterval: 30_000, retry: false });
  const floors = useQuery({ queryKey: ['custody', 'fee-floors'], queryFn: api.feeFloors, refetchInterval: 60_000, retry: false });
  const renewals = useQuery({ queryKey: ['custody', 'renewals'], queryFn: api.renewals, refetchInterval: 30_000, retry: false });

  // Drafts: seeded from the server's read; a newer read re-seeds a group only while its draft is untouched — an edited
  // one is kept and said (lib/seeded-draft.ts; Screens and Astra, 27 Oct 2026, minor).
  const limDraft = useSeededDraft(limits.data ? limitsOf(limits.data) : null);
  const lim = limDraft.draft;
  const setLim = limDraft.set;
  const flDraft = useSeededDraft<Partial<Record<FeeFloorClass, string>>>(floors.data ? { ...floors.data.floors } : null);
  const fl = flDraft.draft;
  const setFl = flDraft.set;
  const daysDraft = useSeededDraft<Partial<Record<RenewalClass, string>>>(renewals.data ? Object.fromEntries(renewals.data.renewals.map((r) => [r.class, String(r.everyDays)])) : null);
  const days = daysDraft.draft ?? {};
  const setDays = daysDraft.set;
  const movedUnder = limDraft.movedUnder || flDraft.movedUnder || daysDraft.movedUnder;

  const changes = useMemo<Change[]>(() => {
    const out: Change[] = [];
    if (limits.data && lim) {
      const base = limitsOf(limits.data);
      for (const k of Object.keys(LIMIT_WORDS) as Array<keyof Limits>) {
        const a = lim[k]; const b = base[k];
        if (typeof a === 'boolean' ? a !== b : a.trim() !== b) out.push({ group: 'limits', label: LIMIT_WORDS[k], before: say(b), after: say(a) });
      }
    }
    if (floors.data && fl) {
      for (const c of CLASSES) {
        const a = (fl[c] ?? '').trim(); const b = floors.data.floors[c] ?? '';
        if (a !== b) out.push({ group: 'floors', label: `${CLASS_WORD[c]} floor`, before: say(b, false), after: say(a, false) });
      }
    }
    for (const r of renewals.data?.renewals ?? []) {
      const a = (days[r.class] ?? '').trim();
      if (a !== String(r.everyDays)) out.push({ group: r.class, label: `${CLASS_WORD[r.class] ?? r.classWord} renewal`, before: `every ${r.everyDays} days`, after: `every ${a || '—'} days` });
    }
    return out;
  }, [limits.data, lim, floors.data, fl, renewals.data, days]);
  const dayProblems = (renewals.data?.renewals ?? []).map((r) => ({ r, p: (days[r.class] ?? '').trim() === String(r.everyDays) ? null : everyDaysProblem(days[r.class] ?? '') })).filter((x) => x.p);

  // A group is saved only from a CURRENT read of it (Screens and Astra on the setup screens, 27 Oct 2026, #2): after a
  // failed or paused refresh the cached values and the draft stay, and Save would post the cached limits. Then the
  // group's changes cannot be reviewed, an open review closes, and the save itself checks again.
  const readOf = (g: Change['group']) => (g === 'limits' ? limits : g === 'floors' ? floors : renewals);
  const current = (g: Change['group']) => readIsCurrent(readOf(g));
  const stale = [...new Set(changes.map((c) => c.group))].filter((g) => !current(g));
  const staleKey = stale.join('|');

  const [reviewing, setReviewing] = useState(false);
  // THE CAPS' VERSION HE REVIEWED AGAINST (P-060): sent as ifUpdatedAt, so a save that came in after it refuses this one (409
  // limits_changed, nothing written) instead of being overwritten without a word.
  const [reviewedAt, setReviewedAt] = useState<string | null>(null);
  const changeKey = JSON.stringify(changes);
  useEffect(() => { setReviewing(false); }, [changeKey, staleKey]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);

  const save = useMutation({
    mutationFn: async () => {
      const done: Outcome[] = [];
      // Only the groups whose read is current as Save is pressed; the others are said, not posted.
      const groups = new Set(changes.map((c) => c.group).filter((g) => current(g)));
      for (const g of new Set(changes.map((c) => c.group))) {
        if (!groups.has(g)) done.push({ group: g, ok: false, line: `${g === 'limits' ? 'Operating limits' : g === 'floors' ? 'Fee floors' : CLASS_WORD[g] ?? g} not saved — its current values could not be read.` });
      }
      if (groups.has('limits') && lim) {
        try {
          await api.setLimits({
            moveCeilingUsdt: lim.move.trim(), dayCeilingUsdt: lim.day.trim(), capsOff: lim.capsOff, secondDevicePayouts: lim.twoDevices, ifUpdatedAt: reviewedAt,
            ...(limits.data?.sources ? { sources: { dirty_hot: { moveCeilingUsdt: lim.mMove.trim(), dayCeilingUsdt: lim.mDay.trim() }, frozen: { moveCeilingUsdt: lim.fMove.trim(), dayCeilingUsdt: lim.fDay.trim() } } } : {}),
          });
          done.push({ group: 'limits', ok: true, line: 'Operating limits saved.' });
        } catch (e) {
          // Changed meanwhile: the caps as they stand become the read (his draft is kept — it is touched), so the review shows
          // them beside his and Save goes again against them.
          const now = limitsChanged(e) ? (e.body as Record<string, unknown>).current : undefined;
          if (now && typeof now === 'object') qc.setQueryData<MoveLimits>(['custody', 'limits'], (old) => ({ ...(old ?? {}), ...(now as MoveLimits) }) as MoveLimits);
          done.push({ group: 'limits', ok: false, line: limitsChanged(e) ? `Operating limits not saved — ${limitsRefusal(e)} The current values are shown; review and save again.` : `Operating limits not saved — ${limitsRefusal(e)}` });
        }
      }
      if (groups.has('floors') && fl) {
        try {
          await api.setFeeFloors(Object.fromEntries(CLASSES.map((c) => [c, (fl[c] ?? '').trim()])) as Record<FeeFloorClass, string>);
          done.push({ group: 'floors', ok: true, line: 'Fee floors saved.' });
        } catch (e) { done.push({ group: 'floors', ok: false, line: `Fee floors not saved — ${limitsRefusal(e)}` }); }
      }
      for (const r of renewals.data?.renewals ?? []) {
        if (!groups.has(r.class)) continue;
        const word = CLASS_WORD[r.class] ?? r.classWord;
        try { await api.setRenewalDays(r.class, Number((days[r.class] ?? '').trim())); done.push({ group: r.class, ok: true, line: `${word}: every ${(days[r.class] ?? '').trim()} days, from the next renewal.` }); }
        catch (e) { done.push({ group: r.class, ok: false, line: `${word} not saved — ${renewalRefusal(e)}` }); }
      }
      return done;
    },
    onSuccess: (done) => {
      setOutcomes(done);
      setReviewing(false);
      // What was saved is what the server holds now: those drafts are untouched again, so the refresh re-seeds them.
      if (done.some((d) => d.group === 'limits' && d.ok)) limDraft.settle();
      if (done.some((d) => d.group === 'floors' && d.ok)) flDraft.settle();
      if (done.some((d) => d.group !== 'limits' && d.group !== 'floors' && d.ok)) daysDraft.settle();
      for (const key of ['limits', 'fee-floors', 'renewals', 'whitelist', 'approvals']) void qc.invalidateQueries({ queryKey: ['custody', key] });
    },
  });

  const anyRead = !!(limits.data || floors.data || renewals.data);
  const canReview = changes.length > 0 && dayProblems.length === 0 && stale.length === 0 && !save.isPending;
  const field = (value: string, set: (v: string) => void, label: string, unit: string) => (
    <label className="block min-w-0">
      <span className="mb-1 block text-meta text-tg-hint">{label} · {unit}</span>
      <input className="input font-mono tabular" inputMode="decimal" value={value} disabled={save.isPending} onChange={(e) => set(e.target.value)} />
    </label>
  );
  const group = (title: string) => <h2 className="border-b border-stroke-soft pb-2 pt-3 text-label font-medium uppercase tracking-wider text-tg-hint">{title}</h2>;

  return (
    <FormFrame
      title="Custody settings"
      head={!embedded}
      aside={(
        <FormAside
          title="Review before saving"
          figure={changes.length === 0 ? 'No changes' : `${changes.length} ${changes.length === 1 ? 'change' : 'changes'}`}
          help={(
            <>
              <p>The caps bound every payout while testing; No caps lifts them on your word, and the 10 USDT test on a new payout address stays either way. Second device approval makes a payout wait for the other registered device (off by default).</p>
              <p>A wallet refills its TRX from Fee under half its floor; a payout from a source under {floors.data?.minTrx ?? '15'} TRX is refused before the Nano. A renewal issues the next address from the class&rsquo;s key and retires the old one, still watched; its balance moves only on your Nano.</p>
            </>
          )}
        />
      )}
      below={embedded ? undefined : <Link to={CA.guide} className="btn-link text-meta">Custody flow</Link>}
    >
      {/* ── Operating limits */}
      {group('Operating limits')}
      {limits.isError ? (
        isCustodySignin(limits.error) ? <SigninQuiet error={limits.error} /> : <StateLine tone="bad" more={limitsRefusal(limits.error)} action={<button type="button" className={TAP} onClick={() => void limits.refetch()}>Try again</button>}>Couldn&rsquo;t read the caps.</StateLine>
      ) : !lim ? (
        <p className="text-body text-tg-hint">Reading…</p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field(lim.move, (v) => setLim({ ...lim, move: v }), 'Per move cap', 'USDT')}
            {field(lim.day, (v) => setLim({ ...lim, day: v }), 'Daily cap', 'USDT')}
            {limits.data?.sources && (
              <>
                {field(lim.mMove, (v) => setLim({ ...lim, mMove: v }), 'Merchant Hot per move', 'USDT')}
                {field(lim.mDay, (v) => setLim({ ...lim, mDay: v }), 'Merchant Hot daily', 'USDT')}
                {field(lim.fMove, (v) => setLim({ ...lim, fMove: v }), 'Flagged per move', 'USDT')}
                {field(lim.fDay, (v) => setLim({ ...lim, fDay: v }), 'Flagged daily', 'USDT')}
              </>
            )}
          </div>
          <label className="flex min-h-11 items-center gap-2 text-body lg:min-h-10">
            <input type="checkbox" className="h-5 w-5" checked={lim.capsOff} disabled={save.isPending} onChange={(e) => setLim({ ...lim, capsOff: e.target.checked })} />
            No caps
          </label>
          <label className="flex min-h-11 items-center gap-2 text-body lg:min-h-10">
            <input type="checkbox" className="h-5 w-5" checked={lim.twoDevices} disabled={save.isPending} onChange={(e) => setLim({ ...lim, twoDevices: e.target.checked })} />
            Second device approval
          </label>
          <p className="flex justify-between gap-3 text-body"><span className="text-meta text-tg-hint">Test to a new payout address</span><span className="whitespace-nowrap"><span className="font-mono tabular">{groupDigits(limits.data?.testPaymentUsdt ?? '10')}</span> <span className="text-meta text-tg-hint">USDT·TRC</span></span></p>
        </div>
      )}

      {/* ── Fee floors */}
      {group('Fee floors')}
      {floors.isError ? (
        isCustodySignin(floors.error) ? <SigninQuiet error={floors.error} /> : <StateLine tone="bad" more={limitsRefusal(floors.error)} action={<button type="button" className={TAP} onClick={() => void floors.refetch()}>Try again</button>}>Couldn&rsquo;t read the fee floors.</StateLine>
      ) : !fl || !floors.data ? (
        <p className="text-body text-tg-hint">Reading…</p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {CLASSES.map((c) => <div key={c}>{field(fl[c] ?? '', (v) => setFl({ ...fl, [c]: v }), CLASS_WORD[c] ?? c, 'TRX')}</div>)}
          </div>
          <p className="text-meta text-tg-hint">
            {floors.data.set ? `Set ${floors.data.updatedAt ? relativeTime(floors.data.updatedAt) : ''} by ${floors.data.updatedBy ?? '—'}` : 'Defaults'}
          </p>
        </div>
      )}

      {/* ── Renewals */}
      {group('Renewals')}
      {renewals.isError ? (
        isCustodySignin(renewals.error) ? <SigninQuiet error={renewals.error} /> : <StateLine tone="bad" more={renewalRefusal(renewals.error)} action={<button type="button" className={TAP} onClick={() => void renewals.refetch()}>Try again</button>}>Couldn&rsquo;t read the renewals.</StateLine>
      ) : !renewals.data ? (
        <p className="text-body text-tg-hint">Reading…</p>
      ) : renewals.data.renewals.length === 0 ? (
        <p className="text-body text-tg-hint">No renewable class yet.</p>
      ) : (
        <div className="space-y-4">
          {renewals.data.renewals.map((r) => (
            <RenewalRowV2 key={r.class} r={r} value={days[r.class] ?? ''} onChange={(v) => setDays({ ...days, [r.class]: v })} disabled={save.isPending}
              problem={dayProblems.find((x) => x.r.class === r.class)?.p ?? null} />
          ))}
        </div>
      )}

      {reviewing && changes.length > 0 && (
        <div className="rounded-lg border border-stroke bg-panel-2 px-4">
          {changes.map((c) => <Fact key={c.label} label={c.label}><span className="text-tg-hint">{c.before}</span> → <span className="font-medium">{c.after}</span></Fact>)}
        </div>
      )}
      {outcomes.length > 0 && !reviewing && (
        <div className="space-y-1">{outcomes.map((o) => <StateLine key={o.group} tone={o.ok ? 'good' : 'bad'}>{o.line}</StateLine>)}</div>
      )}
      {save.isError && <StateLine tone="bad" more={textOf(save.error)}>Not saved — try again.</StateLine>}

      {movedUnder && (
        <StateLine tone="warn" more="Your values are kept. The review shows the values the server holds now beside yours.">Changed elsewhere while you edited.</StateLine>
      )}
      <FormFoot note={!anyRead ? 'Nothing read yet' : stale.length > 0 ? 'Read again before saving' : reviewing ? 'Check every value, then save' : ''}>
        {reviewing && <button type="button" className={QUIET} disabled={save.isPending} onClick={() => setReviewing(false)}>Edit</button>}
        {reviewing ? (
          <button type="button" className={PRIMARY} disabled={!canReview} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save settings'}</button>
        ) : (
          <button type="button" className={PRIMARY} disabled={!canReview} onClick={() => { setOutcomes([]); setReviewedAt(limits.data?.updatedAt ?? null); setReviewing(true); }}>Review settings</button>
        )}
      </FormFoot>
    </FormFrame>
  );
}

/** One renewable class: how often, its line (when, from which account), and Renew now — an action, not a setting. */
function RenewalRowV2({ r, value, onChange, disabled, problem }: { r: RenewalRow; value: string; onChange: (v: string) => void; disabled: boolean; problem: string | null }) {
  const renew = useRenewNow(r);
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <label className="block min-w-0">
          <span className="mb-1 block text-meta text-tg-hint">{CLASS_WORD[r.class] ?? r.classWord} renewal · days</span>
          <input className="input font-mono tabular" inputMode="numeric" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        </label>
        <button
          type="button"
          className="btn-secondary min-h-11 px-4 text-body lg:min-h-10"
          disabled={!r.keyRegistered || renew.stage === 'marking' || renew.stage === 'waiting'}
          title={r.keyRegistered ? undefined : 'Register this class\'s account key first.'}
          onClick={() => { if (window.confirm(renewConfirm(r.class))) renew.renew(); }}
        >
          {renew.stage === 'marking' ? 'Marking…' : renew.stage === 'waiting' ? 'Renewing…' : 'Renew now'}
        </button>
      </div>
      {/* Labelled facts, never one sentence (Astra, 2 Nov 2026): when it renews, which key account it comes from, and the
          address in use — the one a renewal replaces, so the row shows the new one as soon as it is made. */}
      <p className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-meta">
        <span><span className="text-tg-hint">Renews </span>{r.dueInDays === null ? '—' : r.dueInDays === 0 ? 'due now' : `in ${r.dueInDays} ${r.dueInDays === 1 ? 'day' : 'days'}`}</span>
        <span><span className="text-tg-hint">Key account </span><span className="font-mono">{r.keyAccount + 1}</span></span>
        <span className="flex min-w-0 items-baseline gap-1"><span className="text-tg-hint">In use </span>{r.active ? <Address value={r.active.address} /> : <span className="text-tg-hint">none yet</span>}</span>
      </p>
      {!r.keyRegistered && <Link to={CA.register} className="btn-link text-meta">Register device</Link>}
      {problem && <StateLine tone="bad">{problem}</StateLine>}
      {renew.stage === 'failed' && <StateLine tone="bad" more={renewalRefusal(renew.error)}>Not renewed.</StateLine>}
      {renew.stage === 'waiting' && <StateLine>Renewing — waiting for the new address.</StateLine>}
      {renew.stage === 'renewed' && <StateLine tone="good" more="The old address is retired and still watched; the move of its balance appears in Approvals for your Nano.">Renewed — the new address is in use.</StateLine>}
      {renew.stage === 'late' && <StateLine tone="warn" more="Still due: the desk renews it on a later tick, and this row shows the new address when it does.">Not renewed yet.</StateLine>}
    </div>
  );
}

/** A group's draft, seeded from the server's read and re-seeded only while untouched (lib/seeded-draft.ts). */
function useSeededDraft<T>(server: T | null) {
  const [state, setState] = useState<Seeded<T>>(SEEDED_EMPTY);
  const key = server === null ? '' : JSON.stringify(server);
  useEffect(() => { if (server !== null) setState((cur) => reseed(cur, server)); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    draft: state.draft,
    movedUnder: state.movedUnder,
    set: (draft: T) => setState((cur) => ({ ...cur, draft })),
    settle: () => setState((cur) => settled(cur)),
  };
}
